/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext } from 'react';
import { withStyles } from '@material-ui/core';
import {
    array,
    arrayOf,
    bool, number, object, shape, string,
} from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import mbpLogger from '@module-logger/mbp';
import { useSelector } from 'react-redux';
import getPrice from '../../../../../../helpers/getPrice/getPrice';
import FptSkuUpsellMessage from './FptSkuUpsellMessage';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';
import useIsPlaPage from '../../../../../../helpers/hooks/useIsPlaPage';
import DynamicPriceSkeleton from '../../../../GraphqlSkeletonComponents/DynamicPriceSkeleton';
import PriceRange from '../../../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/DesktopSimpleProduct/PriceRange';
import { getPriceArrays } from '../../../../../../helpers/DynamicPricing/getPriceArrays';

const styles = (theme) => ({
    skuLabelAmount: {
        color: theme.cms?.skuLabelAmountTextColor || '#1f1f1b',
        marginLeft: '10px',
        fontWeight: '500',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
            textAlign: 'right',
            '&--retail, &--sale': {
                fontSize: '15px',
            },
        },
        '&--retail': {
            marginLeft: '5px',
            textDecoration: 'line-through',
            color: '#1f1f1b',
            fontWeight: '400',
        },
        '&--sale': {
            marginLeft: '5px',
            color: '#a1001a',
        },
    },
    skuLabelName: {
        color: '#000',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
        },
    },
    PLAFontsRedesign: {
        fontSize: '18px',
        fontWeight: '700',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        color: theme.palette.black || '#000',
        '&--retail': {
            textDecoration: 'line-through',
            color: theme.palette.cms?.red || '#f80404',
        },
        '&--sale': {
            color: theme.palette.cms?.green || '#7DC383',
        },
    },
    PLAMainContainer: {
        display: 'block !important',
        textAlign: 'center',
        fontFamily: 'Helvetica, Arial, sans-serif',
        color: theme.palette.black || '#000000',
        fontSize: '16px',
        fontWeight: '400',
    },
    PLAskuLabelAmount: {
        textAlign: 'center',
        color: theme.palette.colorgrey || '#757575',
        '&--retail': {
            textDecoration: 'line-through',
            color: theme.palette.cms?.red || '#f80404',
        },
        '&--sale': {
            color: theme.palette.cms?.green || '#7DC383',
        },
    },
    containerSelectedCheckbox: {
        position: 'absolute',
        right: '-1px',
        bottom: '-1px',
        height: '20px',
        width: '20px !important',
        backgroundColor: theme.palette.cms?.flowerPdpButton || '#65388b',
    },
    checkRoot: {
        color: theme.palette.white || '#ffffff',
        position: 'absolute',
        top: '-3px',
        left: '3px',
        width: '0.6em',
    },
    brandCheckboxColor: {
        backgroundColor: theme.palette.cms?.flowerPdpButton || '#45b071',
    },
    wrapper: {
        '& $skuLabelName, $skuLabelAmount': {
            fontSize: '16px',
            lineHeight: '24px',
            [theme.breakpoints.up('600')]: {
                fontSize: '18px',
                lineHeight: '26px',
            },
        },
    },
    plaRedesignWrapper: {
        alignItems: 'center',
        '& $skuLabelName, $skuLabelAmount, span': {
            fontSize: '16px',
            lineHeight: '21px',
            [theme.breakpoints.down('600')]: {
                fontSize: '14px',
                lineHeight: '18px',
            },
        },
        '&>span': {
            [theme.breakpoints.down('400')]: {
                width: '60%',
            },
        },
        '& [data-testid="sale-price"]': {
            [theme.breakpoints.down('400')]: {
                width: '40%',
                textAlign: 'right',
            },
        },
        '& $skuLabelAmount': {
            width: '40%',
            textAlign: 'right',
        },
    },
    plaRedesignWrapperVariantB: {
        '& $skuLabelName, $skuLabelAmount, span': {
            fontSize: '16px',
            lineHeight: '21px',
        },
    },
});

const getPassportBundleDetails = (isPassportBundleProduct, sku) => {
    let passportBundlePrice = 0;
    let passportBundleText = '';
    if (isPassportBundleProduct?.enable) {
        passportBundlePrice = Number(isPassportBundleProduct.prices?.value || 0);
        if (sku?.name?.length < 45) {
            passportBundleText = isPassportBundleProduct.skuText?.length ? ` ${isPassportBundleProduct.skuText}` : ' Bundle';
        }
    }
    return {
        passportBundlePrice,
        passportBundleText,
    };
};

const FloralRadioLabelContainer = ({
    sku,
    isPassportBundleProduct,
    classes,
    isUpsellFlagAndABTestEnabled,
    showUpsellMessage,
    pricesList,
    floralPLARedesign,
    isCurrentSkuAsUpsell,
    priceDifferenceBetweenSkuAndUpsell,
    renderSkuPrice,
    isPdpRedesignEnabled,
    productSkuPrices,
    isPlaRedesignEnabled,
}) => {
    if (!Object.keys(sku)?.length) return null;

    const currentSkuRetailPrice = Number((pricesList.length > 0 ? pricesList : sku.prices)?.find((price) => price.type === 'retail')?.value || 0);
    const currentSkuActualPriceDetails = getPrice((pricesList.length > 0 ? pricesList : sku.prices));
    const passportBundleDetails = getPassportBundleDetails(isPassportBundleProduct, sku);

    const totalRetailPrice = currentSkuRetailPrice + passportBundleDetails.passportBundlePrice;
    const retailPrice = `$${parseFloat(totalRetailPrice).toFixed(2)}`;
    const totalSalePrice = currentSkuActualPriceDetails.value + passportBundleDetails.passportBundlePrice;
    const salePrice = `$${parseFloat(totalSalePrice).toFixed(2)}`;
    const skuLabel = ReactHtmlParser(sku.name + passportBundleDetails.passportBundleText);

    if (currentSkuActualPriceDetails?.hasSale && retailPrice === salePrice) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            component: 'FloralRadioLabelContainer.js',
            message: `Error with subscription prices on PDP page when customer selects subscription option, Display price ${retailPrice} is same as Sale Price ${salePrice} }`,
        });
    }
    const isPlaPage = useIsPlaPage();
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const PLAVariantStyle = isPlaPage && floralPLARedesign === 'VARIANT' && ssrDeviceType === 'desktop';
    const PLARetailPriceStyle = PLAVariantStyle ? `${classes.PLAFontsRedesign}${currentSkuActualPriceDetails?.hasSale ? '--retail' : ''}` : '';
    const PLASalePriceStyle = PLAVariantStyle ? `${classes.PLAFontsRedesign}${currentSkuActualPriceDetails?.hasSale ? '--sale' : ''}` : '';
    const productPriceSku = productSkuPrices?.find((item) => item.id === sku.id);
    const priceRender = () => (
        <span>
            <span className={`${classes.skuLabelAmount}${currentSkuActualPriceDetails?.hasSale ? '--retail' : ''} ${PLARetailPriceStyle}`}>{retailPrice}</span>
            {currentSkuActualPriceDetails?.hasSale && <span className={`${PLASalePriceStyle} ${classes.skuLabelAmount}--sale`}><strong>{salePrice}</strong></span>}
        </span>

    );
    return (
        <div className={`${isPdpRedesignEnabled ? classes.wrapper : ''} ${isPlaRedesignEnabled ? classes.plaRedesignWrapper : ' '}`}>
            <span>
                <span className={classes.skuLabelName} aria-hidden="true">{skuLabel}</span>
                {
                    isUpsellFlagAndABTestEnabled ? (
                        <FptSkuUpsellMessage
                            showUpsellMessage={showUpsellMessage}
                            isCurrentSkuAsUpsell={isCurrentSkuAsUpsell}
                            priceDifferenceBetweenSkuAndUpsell={priceDifferenceBetweenSkuAndUpsell}
                        />
                    ) : null
                }
            </span>
            {renderSkuPrice ? (
                priceRender()
            ) : (
                <span><DynamicPriceSkeleton priceLineOnly /></span>
            )}
        </div>
    );
};

FloralRadioLabelContainer.propTypes = {
    classes: object.isRequired,
    isPassportBundleProduct: shape({
        enable: bool,
        prices: shape({
            value: number,
        }),
        skuText: string,
    }),
    sku: shape({
        prices: arrayOf(shape({
            type: string,
            value: number,
        })).isRequired,
    }).isRequired,
    isUpsellFlagAndABTestEnabled: bool.isRequired,
    showUpsellMessage: bool.isRequired,
    pricesList: arrayOf(shape({
        value: number,
        type: string,
    })),
    floralPLARedesign: string,
    isCurrentSkuAsUpsell: bool.isRequired,
    priceDifferenceBetweenSkuAndUpsell: number.isRequired,
    renderSkuPrice: bool,
    isPdpRedesignEnabled: bool,
    productSkuPrices: array,
    isPlaRedesignEnabled: bool,
};

FloralRadioLabelContainer.defaultProps = {
    isPassportBundleProduct: {},
    pricesList: [],
    floralPLARedesign: '',
    renderSkuPrice: true,
    isPdpRedesignEnabled: false,
    productSkuPrices: [],
    isPlaRedesignEnabled: false,
};

export default withStyles(styles)(FloralRadioLabelContainer);
